import React, { useEffect, useMemo, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, Crown, Star, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { SectionHeader } from '../ui/SectionHeader';
import { DjayAvatar } from '../../mascot';
import { getAchievementIllustration } from '../../mascot';
import type { DjayEmotion, DjayVariant, DjayAccessory } from '../../mascot';
import { AppState } from '../../app/types';
import { MODULES } from '../../content';
interface AchievementsSectionProps {
  state: AppState;
  totalQuestionsAnswered: number;
  masteredCount: number;
  currentStreak: number;
  onNavigate: (view: string) => void;
}
type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'legendary';
export interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: 'onboarding' | 'streak' | 'mastery' | 'level' | 'special';
  rarity: AchievementRarity;
  djayEmotionUnlocked: DjayEmotion;
  djayEmotionLocked: DjayEmotion;
  djayVariantUnlocked?: DjayVariant;
  accessory: DjayAccessory;
  check: (data: AchievementCheckData) => boolean;
  getProgress?: (data: AchievementCheckData) => {
    current: number;
    max: number;
    label: string;
  };
}
export interface AchievementCheckData {
  totalQuestionsAnswered: number;
  masteredCount: number;
  level: number;
  xp: number;
  // Dagelijkse streak (§15)
  currentWeekDays: number; // 0-5, schooldagen deze week
  consecutiveFullWeeks: number; // Opeenvolgende volle weken
  // Antwoord-streak (§14)
  longestTotalStreak: number; // Persoonlijk record correct-op-rij
  // Dagelijks doel
  totalDailyGoalsReached: number; // Lifetime count
  // Perfecte sessies
  totalPerfectSessions: number; // Lifetime count
}
export const ACHIEVEMENTS: Achievement[] = [
// Eerste Stappen (Onboarding)
{
  id: 'first-steps',
  name: 'Eerste Stappen',
  description: 'Beantwoord 5 vragen',
  emoji: '🌱',
  category: 'onboarding',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'seedling',
  check: data => data.totalQuestionsAnswered >= 5,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 5),
    max: 5,
    label: 'vragen'
  })
}, {
  id: 'getting-started',
  name: 'Lekker Bezig',
  description: 'Beantwoord 50 vragen',
  emoji: '📝',
  category: 'onboarding',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'pencil',
  check: data => data.totalQuestionsAnswered >= 50,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 50),
    max: 50,
    label: 'vragen'
  })
}, {
  id: 'century',
  name: 'Honderdtal',
  description: 'Beantwoord 100 vragen',
  emoji: '💯',
  category: 'onboarding',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'pixel',
  accessory: 'badge100',
  check: data => data.totalQuestionsAnswered >= 100,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 100),
    max: 100,
    label: 'vragen'
  })
},
// Reeksen (Streaks)
{
  id: 'three-days',
  name: 'Drie Dagen',
  description: 'Oefen 3 schooldagen deze week',
  emoji: '🔥',
  category: 'streak',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'smallFlame',
  check: data => data.currentWeekDays >= 3,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 3),
    max: 3,
    label: 'dagen'
  })
}, {
  id: 'week-champion',
  name: 'Week Kampioen',
  description: 'Oefen een hele schoolweek (5/5)',
  emoji: '🏆',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.currentWeekDays >= 5,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 5),
    max: 5,
    label: 'dagen'
  })
}, {
  id: 'fire-master',
  name: 'Vuurmeester',
  description: 'Oefen 3 volle weken achter elkaar',
  emoji: '🌋',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'blaze',
  accessory: 'doubleFlameCrown',
  check: data => data.consecutiveFullWeeks >= 3,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 3),
    max: 3,
    label: 'weken'
  })
}, {
  id: 'hot-streak',
  name: 'Op Dreef',
  description: '10 correct op rij',
  emoji: '⚡',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'smallFlame',
  check: data => data.longestTotalStreak >= 10,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 10),
    max: 10,
    label: 'op rij'
  })
}, {
  id: 'unstoppable',
  name: 'Onstopbaar',
  description: '25 correct op rij',
  emoji: '🌟',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'doubleFlameCrown',
  check: data => data.longestTotalStreak >= 25,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 25),
    max: 25,
    label: 'op rij'
  })
}, {
  id: 'legendary-streak',
  name: 'Legendarische Reeks',
  description: '30 correct op rij',
  emoji: '👑',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'scepter',
  check: data => data.longestTotalStreak >= 30,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 30),
    max: 30,
    label: 'op rij'
  })
}, {
  id: 'unbreakable',
  name: 'Onbreekbaar',
  description: '10 volle weken achter elkaar',
  emoji: '💎',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'blaze',
  accessory: 'doubleFlameCrown',
  check: data => data.consecutiveFullWeeks >= 10,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 10),
    max: 10,
    label: 'weken'
  })
},
// Meesterschap (Mastery)
{
  id: 'first-subject',
  name: 'Eerste Vak Klaar',
  description: 'Rond je eerste vak af',
  emoji: '⭐',
  category: 'mastery',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'encouraging',
  accessory: 'starWand',
  check: data => data.masteredCount >= 1,
  getProgress: data => ({
    current: Math.min(data.masteredCount, 1),
    max: 1,
    label: 'vak'
  })
}, {
  id: 'three-subjects',
  name: 'Drie Vakken',
  description: 'Rond 3 vakken af',
  emoji: '🌟',
  category: 'mastery',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'pointing',
  djayVariantUnlocked: 'frost',
  accessory: 'tripleStars',
  check: data => data.masteredCount >= 3,
  getProgress: data => ({
    current: Math.min(data.masteredCount, 3),
    max: 3,
    label: 'vakken'
  })
}, {
  id: 'all-subjects',
  name: 'Alle Vakken',
  description: `Rond alle ${MODULES.length} vakken af`,
  emoji: '👑',
  category: 'mastery',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'royal',
  accessory: 'scepter',
  check: data => data.masteredCount >= MODULES.length,
  getProgress: data => ({
    current: Math.min(data.masteredCount, MODULES.length),
    max: MODULES.length,
    label: 'vakken'
  })
},
// Levels
{
  id: 'level-5',
  name: 'Kenner',
  description: 'Bereik level 5',
  emoji: '🌿',
  category: 'level',
  rarity: 'common',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'leafGarland',
  check: data => data.level >= 5,
  getProgress: data => ({
    current: Math.min(data.level, 5),
    max: 5,
    label: 'levels'
  })
}, {
  id: 'level-10',
  name: 'Kampioen',
  description: 'Bereik level 10',
  emoji: '🌟',
  category: 'level',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'medal',
  check: data => data.level >= 10,
  getProgress: data => ({
    current: Math.min(data.level, 10),
    max: 10,
    label: 'levels'
  })
}, {
  id: 'level-15',
  name: 'Ultieme Meester',
  description: 'Bereik level 15',
  emoji: '🌌',
  category: 'level',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'sleeping',
  djayVariantUnlocked: 'cosmic',
  accessory: 'cosmicOrb',
  check: data => data.level >= 15,
  getProgress: data => ({
    current: Math.min(data.level, 15),
    max: 15,
    label: 'levels'
  })
},
// Special
{
  id: 'shadow-realm',
  name: 'Schaduw Meester',
  description: 'Behaal 30.000 XP',
  emoji: '🌑',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'surprised',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'shadow',
  accessory: 'shadowOrb',
  check: data => data.xp >= 30000,
  getProgress: data => ({
    current: Math.min(data.xp, 30000),
    max: 30000,
    label: 'XP'
  })
}, {
  id: 'goal-getter',
  name: 'Doelpakker',
  description: 'Bereik 10x je dagelijks doel',
  emoji: '🎯',
  category: 'special',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'thinking',
  accessory: 'medal',
  check: data => data.totalDailyGoalsReached >= 10,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 10),
    max: 10,
    label: 'keer'
  })
}, {
  id: 'consistent',
  name: 'Consistent',
  description: 'Bereik 50x je dagelijks doel',
  emoji: '📅',
  category: 'special',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.totalDailyGoalsReached >= 50,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 50),
    max: 50,
    label: 'keer'
  })
}, {
  id: 'perfectionist',
  name: 'Perfectionist',
  description: '5 perfecte sessies',
  emoji: '🏆',
  category: 'special',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.totalPerfectSessions >= 5,
  getProgress: data => ({
    current: Math.min(data.totalPerfectSessions, 5),
    max: 5,
    label: 'sessies'
  })
}, {
  id: 'rainbow-djay',
  name: 'Regenboog Verzamelaar',
  description: 'Verzamel alle 6 leer-mascottes (Pixel, Blaze, Frost, Royal, Cosmic, Shadow)',
  emoji: '🌈',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'rainbow',
  accessory: 'tripleStars',
  check: data => data.totalQuestionsAnswered >= 100 && data.consecutiveFullWeeks >= 3 && data.masteredCount >= MODULES.length && data.level >= 15 && data.xp >= 30000,
  getProgress: data => {
    const unlocked = [data.totalQuestionsAnswered >= 100, data.consecutiveFullWeeks >= 3, data.masteredCount >= 3, data.masteredCount >= MODULES.length, data.level >= 15, data.xp >= 30000].filter(Boolean).length;
    return {
      current: unlocked,
      max: 6,
      label: 'mascottes'
    };
  }
}];
export const CATEGORY_LABELS: Record<Achievement['category'], string> = {
  onboarding: 'Eerste Stappen',
  streak: 'Reeksen',
  mastery: 'Meesterschap',
  level: 'Levels',
  special: 'Speciaal'
};
export const RARITY_CONFIG: Record<AchievementRarity, {
  label: string;
  cardBg: string;
  cardBorder: string;
  glowColor: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
}> = {
  common: {
    label: 'Gewoon',
    cardBg: 'bg-white',
    cardBorder: 'border-slate-200',
    glowColor: '',
    textColor: 'text-slate-600',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-500'
  },
  uncommon: {
    label: 'Ongewoon',
    cardBg: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    cardBorder: 'border-emerald-200',
    glowColor: 'shadow-emerald-200/50',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-600'
  },
  rare: {
    label: 'Zeldzaam',
    cardBg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50',
    cardBorder: 'border-amber-300',
    glowColor: 'shadow-amber-300/60',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700'
  },
  legendary: {
    label: 'Legendarisch',
    cardBg: 'bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50',
    cardBorder: 'border-purple-300',
    glowColor: 'shadow-purple-400/50',
    textColor: 'text-purple-700',
    badgeBg: 'bg-gradient-to-r from-purple-500 to-fuchsia-500',
    badgeText: 'text-white'
  }
};
// Hook for tracking newly seen achievements and triggering a one-shot shimmer
function useAchievementShimmer(achievementId: string, isUnlocked: boolean) {
  const [isNewlySeen, setIsNewlySeen] = useState(false);
  useEffect(() => {
    if (!isUnlocked) return;
    try {
      const stored = localStorage.getItem('djay_seen_achievements');
      const seenSet = new Set<string>(stored ? JSON.parse(stored) : []);
      if (!seenSet.has(achievementId)) {
        seenSet.add(achievementId);
        localStorage.setItem('djay_seen_achievements', JSON.stringify(Array.from(seenSet)));
        setIsNewlySeen(true);
        const timer = setTimeout(() => setIsNewlySeen(false), 2000);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      console.error('Failed to update seen achievements', e);
    }
  }, [achievementId, isUnlocked]);
  return isNewlySeen;
}
// Sparkle particle component for rare/legendary
export function SparkleParticles({
  count = 6,
  color = 'amber'
}: {
  count?: number;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    amber: 'bg-amber-400',
    purple: 'bg-purple-400',
    fuchsia: 'bg-fuchsia-400'
  };
  // Stable random positions — computed once per mount, not on every re-render
  const positions = useMemo(() => Array.from({
    length: count
  }).map(() => ({
    left: `${15 + Math.random() * 70}%`,
    top: `${15 + Math.random() * 70}%`
  })), [count]);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1612">
      {positions.map((pos, i) => <motion.div key={i} className={`absolute w-1.5 h-1.5 rounded-full ${colorMap[color] || colorMap.amber}`} style={pos} animate={{
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      y: [0, -10, -20]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: i * 0.3,
      ease: 'easeOut'
    }} data-id="element-1613" />)}
    </div>;
}
// Golden shimmer effect for rare achievements
export function GoldenShimmer() {
  return <motion.div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} data-id="element-1614">
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 3,
      repeat: Infinity,
      repeatDelay: 2,
      ease: 'easeInOut'
    }} data-id="element-1615" />
    </motion.div>;
}
// Legendary pulsing glow
export function LegendaryGlow() {
  return <motion.div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-30 blur-md pointer-events-none" animate={{
    opacity: [0.2, 0.4, 0.2],
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-1616" />;
}
// Djay wrapper with rarity-specific visual treatment
export function RarityDjayWrapper({
  rarity,
  emotion,
  variant = 'default',
  accessory = 'none',
  isUnlocked,
  animate
}: {
  rarity: AchievementRarity;
  emotion: DjayEmotion;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  isUnlocked: boolean;
  animate: boolean;
}) {
  // Always use 'sm' for compact cards
  const baseSize = 'sm';
  if (!isUnlocked) {
    return <div className="relative" data-id="element-1617">
        <div className="grayscale opacity-50" data-id="element-1618">
          <DjayAvatar size={baseSize} emotion={emotion} variant="default" accessory="none" animate={false} data-id="element-1619" />
        </div>
      </div>;
  }
  switch (rarity) {
    case 'legendary':
      return <div className="relative" data-id="element-1620">
          {/* Pulsing glow ring */}
          <motion.div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-40 blur-sm" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1621" />
          {/* Crown overlay */}
          <motion.div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 text-2xl" animate={{
          y: [0, -3, 0],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1622">
            👑
          </motion.div>
          <div className="relative z-10" data-id="element-1623">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1624" />
          </div>
        </div>;
    case 'rare':
      return <div className="relative" data-id="element-1625">
          {/* Golden glow ring */}
          <motion.div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-50 blur-sm" animate={{
          opacity: [0.4, 0.6, 0.4]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1626" />
          <div className="relative z-10" data-id="element-1627">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1628" />
          </div>
          {/* Star sparkle */}
          <motion.div className="absolute -top-1 -right-1 z-20" animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1629">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1630" />
          </motion.div>
        </div>;
    case 'uncommon':
      return <div className="relative" data-id="element-1631">
          {/* Subtle glow ring */}
          <div className="absolute -inset-1 rounded-full bg-emerald-300/30 blur-sm" data-id="element-1632" />
          <div className="relative z-10" data-id="element-1633">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1634" />
          </div>
        </div>;
    default:
      return <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1635" />;
  }
}
export const AchievementsSection = memo(function AchievementsSection({
  state,
  totalQuestionsAnswered,
  masteredCount,
  currentStreak,
  onNavigate
}: AchievementsSectionProps) {
  // Extract streak data from state
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  // Lifetime counters from state (HC-7/HC-8 fix: now properly tracked)
  // Fallback: derive totalDailyGoalsReached from weeklyHistory for pre-migration data
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const checkData: AchievementCheckData = useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions]);
  const achievementsByCategory = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  const unlockedCount = achievementsByCategory.filter(a => a.isUnlocked).length;
  const totalCount = achievementsByCategory.length;
  // Compute focus sections
  const {
    recentlyUnlocked
  } = useMemo(() => {
    // Recently unlocked: unlocked achievements, prioritize rare/legendary
    const unlocked = achievementsByCategory.filter(a => a.isUnlocked);
    const rarityWeight = {
      legendary: 4,
      rare: 3,
      uncommon: 2,
      common: 1
    };
    const sortedUnlocked = unlocked.sort((a, b) => rarityWeight[b.rarity] - rarityWeight[a.rarity]);
    const recentlyUnlockedList = sortedUnlocked.slice(0, 3);
    return {
      recentlyUnlocked: recentlyUnlockedList
    };
  }, [achievementsByCategory]);
  const progressPercentage = Math.round(unlockedCount / totalCount * 100);
  return <Card className="p-5 sm:p-6 bg-white border-2 border-slate-100 shadow-sm" data-id="element-1636">
      <SectionHeader title="Achievements" icon={Crown} accentColor="bg-amber-100 text-amber-600" description={`${unlockedCount} van de ${totalCount} behaald`} action={<Button onClick={() => onNavigate('collection')} className="w-full sm:w-auto bg-amber-100 text-amber-700 hover:bg-amber-200 border-none" data-id="element-1638">
            Bekijk alle <ArrowRight className="w-4 h-4 ml-2" data-id="element-1639" />
          </Button>} className="mb-6" data-id="element-1637" />
      <div className="flex-1 w-full" data-id="element-1640">
        {recentlyUnlocked.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1641">
            {recentlyUnlocked.map(achievement => <div key={achievement.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5" data-id="element-1642">
                <span className="text-sm" data-id="element-1643">{achievement.emoji}</span>
                <span className="text-xs font-bold text-slate-700 truncate max-w-[100px]" data-id="element-1644">
                  {achievement.name}
                </span>
              </div>)}
          </div> : <p className="text-sm text-slate-400 italic" data-id="element-1645">
            Nog geen achievements behaald.
          </p>}
      </div>
    </Card>;
});
export interface AchievementCardProps {
  achievement: Achievement & {
    isUnlocked: boolean;
    progress?: {
      current: number;
      max: number;
      label: string;
    };
  };
  isUnlocked: boolean;
  index: number;
}
export function CompactAchievementCard({
  achievement,
  isUnlocked,
  index,
  showEffects
}: AchievementCardProps & {
  showEffects: boolean;
}) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const djayEmotion = isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked;
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: index * 0.1
  }} className={`relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${isUnlocked ? `${config.cardBg} ${config.cardBorder} ${showEffects && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') ? `shadow-md ${config.glowColor}` : 'shadow-sm'}` : 'bg-slate-50 border-slate-200 opacity-80'} ${isNewlySeen ? 'animate-shine' : ''}`} style={isNewlySeen ? {
    animationIterationCount: 1
  } : undefined} data-id="element-1646">
      {isUnlocked && showEffects && achievement.rarity === 'legendary' && <LegendaryGlow data-id="element-1647" />}
      {isUnlocked && showEffects && achievement.rarity === 'rare' && <GoldenShimmer data-id="element-1648" />}

      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-white/50 rounded-xl" data-id="element-1649">
        <span className="text-2xl" data-id="element-1650">{achievement.emoji}</span>
      </div>

      <div className="flex-1 min-w-0" data-id="element-1651">
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1652">
          <h4 className={`font-bold text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1653">
            {achievement.name}
          </h4>
          {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1654">
                {config.label}
              </span>}
        </div>

        {!isUnlocked && achievement.progress ? <div className="w-full" data-id="element-1655">
            <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-1" data-id="element-1656">
              <span data-id="element-1657">{achievement.description}</span>
              <span data-id="element-1658">
                {achievement.progress.current}/{achievement.progress.max}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1659">
              <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
            width: `${achievement.progress.current / achievement.progress.max * 100}%`
          }} data-id="element-1660" />
            </div>
          </div> : <p className={`text-xs truncate ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1661">
            {achievement.description}
          </p>}
      </div>
    </motion.div>;
}
export function AchievementCard({
  achievement,
  isUnlocked,
  index
}: AchievementCardProps) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const djayEmotion = isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1662">
      <Card className={`
          relative overflow-hidden p-3 transition-all duration-300 border-2 h-full flex flex-col
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
          ${isNewlySeen ? 'animate-shine' : ''}
        `} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1663">
        {!isUnlocked && <div className="absolute top-2 right-2 z-10" data-id="element-1664">
            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shadow-inner" data-id="element-1665">
              <Lock className="w-3 h-3 text-slate-400" data-id="element-1666" />
            </div>
          </div>}

        {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <div className="absolute top-2 right-2 z-10" data-id="element-1667">
              <div className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1668">
                {config.label}
              </div>
            </div>}

        <div className="flex items-start gap-3 relative z-10 flex-1" data-id="element-1669">
          <div className="flex-shrink-0" data-id="element-1670">
            <RarityDjayWrapper rarity={achievement.rarity} emotion={djayEmotion} variant={achievement.djayVariantUnlocked} accessory={achievement.accessory} isUnlocked={isUnlocked} animate={isUnlocked} data-id="element-1671" />
          </div>

          <div className="flex-1 min-w-0 pt-0.5 flex flex-col h-full" data-id="element-1672">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-1673">
              <span className="text-base" data-id="element-1674">{achievement.emoji}</span>
              <h4 className={`font-black text-xs truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1675">
                {achievement.name}
              </h4>
            </div>
            <p className={`text-[10px] leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1676">
              {achievement.description}
            </p>

            {!isUnlocked && achievement.progress && <div className="mt-auto pt-1" data-id="element-1677">
                <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-0.5" data-id="element-1678">
                  <span data-id="element-1679">Voortgang</span>
                  <span data-id="element-1680">
                    {achievement.progress.current} / {achievement.progress.max}
                  </span>
                </div>
                <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1681">
                  <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
                width: `${achievement.progress.current / achievement.progress.max * 100}%`
              }} data-id="element-1682" />
                </div>
              </div>}
          </div>
        </div>
      </Card>
    </motion.div>;
}
export function CollectibleAchievementCard({
  achievement,
  isUnlocked,
  index,
  compact = false
}: AchievementCardProps & {
  compact?: boolean;
}) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const Illustration = getAchievementIllustration(achievement.id);
  if (!Illustration) return null;
  if (compact) {
    return <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: index * 0.1
    }} className={`relative flex items-center gap-3 p-2 rounded-2xl border-2 transition-all overflow-hidden ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-80'} ${isNewlySeen ? 'animate-shine' : ''}`} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1683">
        {isUnlocked && achievement.rarity === 'legendary' && <LegendaryGlow data-id="element-1684" />}
        {isUnlocked && achievement.rarity === 'rare' && <GoldenShimmer data-id="element-1685" />}

        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden shadow-inner bg-white/50 relative z-10" data-id="element-1686">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="sm" className="w-full h-full" data-id="element-1687" />
        </div>

        <div className="flex-1 min-w-0 relative z-10 py-1" data-id="element-1688">
          <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1689">
            <h4 className={`font-bold text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1690">
              {achievement.name}
            </h4>
            {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1691">
                  {config.label}
                </span>}
          </div>

          {!isUnlocked && achievement.progress ? <div className="w-full" data-id="element-1692">
              <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-1" data-id="element-1693">
                <span data-id="element-1694">{achievement.description}</span>
                <span data-id="element-1695">
                  {achievement.progress.current}/{achievement.progress.max}
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1696">
                <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
              width: `${achievement.progress.current / achievement.progress.max * 100}%`
            }} data-id="element-1697" />
              </div>
            </div> : <p className={`text-xs truncate ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1698">
              {achievement.description}
            </p>}
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1699">
      <Card className={`
          relative overflow-hidden transition-all duration-300 border-2 h-full flex flex-col p-0
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
          ${isNewlySeen ? 'animate-shine' : ''}
        `} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1700">
        {/* Illustration Header (60% of card) */}
        <div className="relative w-full aspect-video bg-white/40 border-b-2 border-white/20" data-id="element-1701">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="lg" className="w-full h-full" data-id="element-1702" />

          {!isUnlocked && <div className="absolute top-2 right-2 z-30" data-id="element-1703">
              <div className="w-6 h-6 rounded-full bg-slate-800/50 flex items-center justify-center backdrop-blur-sm" data-id="element-1704">
                <Lock className="w-3.5 h-3.5 text-white" data-id="element-1705" />
              </div>
            </div>}

          {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <div className="absolute top-2 right-2 z-30" data-id="element-1706">
                <div className={`px-2 py-1 rounded-full text-[10px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1707">
                  {config.label}
                </div>
              </div>}
        </div>

        {/* Content Footer (40% of card) */}
        <div className="p-3 flex-1 flex flex-col relative z-10" data-id="element-1708">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1709">
            <h4 className={`font-black text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1710">
              {achievement.name}
            </h4>
          </div>
          <p className={`text-xs leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1711">
            {achievement.description}
          </p>

          {!isUnlocked && achievement.progress && <div className="mt-auto pt-2 border-t border-slate-200/50" data-id="element-1712">
              <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1713">
                <span data-id="element-1714">Voortgang</span>
                <span data-id="element-1715">
                  {achievement.progress.current} / {achievement.progress.max}
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1716">
                <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
              width: `${achievement.progress.current / achievement.progress.max * 100}%`
            }} data-id="element-1717" />
              </div>
            </div>}
        </div>
      </Card>
    </motion.div>;
}